import { useEffect, useMemo, useState } from 'react';
import { useSession } from '../lib/session';
import { Bars } from './bars';
import { Card } from './ui';

interface DayCounts {
  date: string;
  visits: number;
  add_calendar: number;
  share_event: number;
  save_event: number;
}

const nf = new Intl.NumberFormat('it-IT');
const LABELS: Array<[keyof Omit<DayCounts, 'date'>, string]> = [
  ['visits', 'Visite'],
  ['add_calendar', 'Al calendario'],
  ['share_event', 'Condivisioni'],
  ['save_event', 'Salvati'],
];

const isoDay = (d: Date) => d.toISOString().slice(0, 10);

/**
 * Statistiche dell'evento (Lambda stats, aggiornate ogni ora): visite della pagina, aggiunte al
 * calendario, condivisioni e salvataggi, negli ultimi 30 giorni e in tutto. Gli stessi dati
 * serviranno agli organizzatori (Fase 3).
 */
export function EventStats({ eventId }: { eventId: string }) {
  const { api } = useSession();
  const [days, setDays] = useState<DayCounts[] | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    api<{ days: DayCounts[] }>(`/events/${eventId}/stats`)
      .then((r) => setDays(r.days))
      .catch(() => setFailed(true));
  }, [api, eventId]);

  const view = useMemo(() => {
    if (!days) return null;
    const since = isoDay(new Date(Date.now() - 29 * 86_400_000));
    const by = new Map(days.map((d) => [d.date, d]));
    const series: Array<{ label: string; value: number; extra: number }> = [];
    for (let i = 29; i >= 0; i--) {
      const d = isoDay(new Date(Date.now() - i * 86_400_000));
      const c = by.get(d);
      series.push({ label: d, value: c?.visits ?? 0, extra: c?.add_calendar ?? 0 });
    }
    const sum = (list: DayCounts[], k: keyof Omit<DayCounts, 'date'>) =>
      list.reduce((n, d) => n + d[k], 0);
    const recent = days.filter((d) => d.date >= since);
    return { series, recent, sum };
  }, [days]);

  return (
    <Card title="Statistiche">
      {failed ? (
        <p className="text-xs text-muted">Statistiche non disponibili.</p>
      ) : !view || !days ? (
        <p className="text-xs text-muted">Caricamento…</p>
      ) : days.length === 0 ? (
        <p className="text-xs text-muted">
          Ancora nessuna visita registrata. Le statistiche si aggiornano ogni ora.
        </p>
      ) : (
        <>
          <table className="w-full text-xs">
            <thead className="text-left text-muted">
              <tr>
                <th className="font-normal" />
                <th className="text-right font-normal">30 giorni</th>
                <th className="text-right font-normal">In tutto</th>
              </tr>
            </thead>
            <tbody>
              {LABELS.map(([k, label]) => (
                <tr key={k} className="border-t border-surface-strong">
                  <td className="py-1">{label}</td>
                  <td className="py-1 text-right tabular-nums">
                    {nf.format(view.sum(view.recent, k))}
                  </td>
                  <td className="py-1 text-right font-medium tabular-nums">
                    {nf.format(view.sum(days, k))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <Bars
            data={view.series}
            legend="visite al giorno (barra) · aggiunte al calendario (segno)"
            valueLabel="visite"
            extraLabel="al calendario"
            className="h-24"
          />
        </>
      )}
    </Card>
  );
}
